import { customerMarketShopfrontApi, Site } from '@envato/foundation-types'
import { ThunkAction } from 'redux-thunk'

import { setSeoMeta } from '../actions/common'
import { reportPageType } from '../analytics'
import { reportProductsImpressions } from '../analytics/resultAnalytics'
import { getBookmarksForItems } from '../actions/user/bookmark'
import { IRootState } from '../reducers'
import axiosVerbose from '../utils/axios/axiosVerbose'
import { logError } from '../utils/errors'
import { ICommonLocation, ILocationParams } from '../utils/location/types'

export enum TypeKeys {
  GET_CATEGORY_PAGE_API_REQUEST = 'GET_CATEGORY_PAGE_API_REQUEST',
  GET_CATEGORY_PAGE_API_SUCCESS = 'GET_CATEGORY_PAGE_API_SUCCESS',
  GET_CATEGORY_PAGE_API_FAIL = 'GET_CATEGORY_PAGE_API_FAIL'
}

export interface IGetCategoryPageApiRequestAction {
  type: TypeKeys.GET_CATEGORY_PAGE_API_REQUEST
}

export interface IGetCategoryPageApiSuccessAction {
  type: TypeKeys.GET_CATEGORY_PAGE_API_SUCCESS
  payload: Readonly<customerMarketShopfrontApi.categoryPage.IResult>
}

export interface IGetCategoryPageApiFailAction {
  type: TypeKeys.GET_CATEGORY_PAGE_API_FAIL
}

export type ActionTypes =
  | IGetCategoryPageApiRequestAction
  | IGetCategoryPageApiSuccessAction
  | IGetCategoryPageApiFailAction

const blocksThatContainItems = ['ItemsBlock', 'PromoItemsBlock']

export const blockContainsItems = (
  block: customerMarketShopfrontApi.categoryPage.ICategoryPageBlock
): block is customerMarketShopfrontApi.categoryPage.ICategoryPageBlock<
  customerMarketShopfrontApi.categoryPage.SearchItemsBlockData
> => {
  return (
    blocksThatContainItems.includes(block.componentType) &&
    block.data &&
    block.data.length > 0
  )
}

const getItemIdsFromBlocks = (
  blocks: customerMarketShopfrontApi.categoryPage.ICategoryPageBlock[]
) =>
  blocks
    .filter(blockContainsItems)
    .map(block => block.data)
    .reduce((items, data) => items.concat(data))
    .map(item => item.id)

const reportBlocks = (
  blocks: customerMarketShopfrontApi.categoryPage.ICategoryPageBlock[],
  currentSite: Site
) => {
  blocks.forEach(block => {
    if (blockContainsItems(block)) {
      reportProductsImpressions({
        items: block.data,
        currentSite,
        itemRankOffset: 1,
        page: 'Category Page',
        section: block.title
      })
    }
  })
}

export const onBeforeCategoryPageView = (): ThunkAction<
  void,
  IRootState,
  void
> => (dispatch, getState) => {
  const blocks = getState().categoryPage.blocks
  const currentSite = getState().common.currentSite

  reportPageType('category')

  reportBlocks(blocks, currentSite)
}

export const fetchCategoryPageDataRequest = () => {
  const action: IGetCategoryPageApiRequestAction = {
    type: TypeKeys.GET_CATEGORY_PAGE_API_REQUEST
  }

  return action
}

export const fetchCategoryPageDataSuccess = (payload: any) => {
  const action: IGetCategoryPageApiSuccessAction = {
    payload,
    type: TypeKeys.GET_CATEGORY_PAGE_API_SUCCESS
  }

  return action
}

export const fetchCategoryPageDataFail = () => {
  const action: IGetCategoryPageApiFailAction = {
    type: TypeKeys.GET_CATEGORY_PAGE_API_FAIL
  }

  return action
}

export const fetchCategoryPageData = ({
  location,
  apiAppUrlPrefix,
  locationParams
}: {
  location: ICommonLocation
  apiAppUrlPrefix: string
  locationParams: ILocationParams
}): ThunkAction<Promise<ActionTypes>, IRootState, void> => {
  return (dispatch, getState) => {
    const { currentSite } = getState().common
    const category = locationParams.category || 'all'

    dispatch(fetchCategoryPageDataRequest())

    return axiosVerbose
      .get<customerMarketShopfrontApi.categoryPage.IResult>(
        `${apiAppUrlPrefix}/shopfront-api/category_page/${currentSite}/${category}`
      )
      .then(response => {
        if (response.data.metadata) {
          dispatch(setSeoMeta(response.data.metadata))
        }

        dispatch(
          getBookmarksForItems(getItemIdsFromBlocks(response.data.blocks))
        )

        return dispatch(fetchCategoryPageDataSuccess(response.data))
      })
      .catch(err => Promise.reject(logError(err)))
      .catch(() => {
        return dispatch(fetchCategoryPageDataFail())
      })
  }
}
